import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material';
import { ArrowBack, Save, Psychology, Person } from '@mui/icons-material';

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  employment_type: string;
}

interface PositionTemplate {
  id: number;
  title: string;
  department: string;
  description: string;
}

interface PIProfile {
  id: number;
  name: string;
  code: string;
  description: string;
}

const AddCandidate: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  
  const [job, setJob] = useState<Job | null>(null);
  const [positionTemplates, setPositionTemplates] = useState<PositionTemplate[]>([]);
  const [piProfiles, setPiProfiles] = useState<PIProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_id: parseInt(jobId || '0'),
    position_template_id: '',
    pi_profile_id: '',
    pi_factor_a: '',
    pi_factor_b: '',
    pi_factor_c: '',
    pi_factor_d: '',
    interview_date: '',
    application_date: new Date().toISOString().split('T')[0],
    application_source: 'other',
    notes: '',
  });

  useEffect(() => {
    if (jobId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch job details
      const jobResponse = await fetch(`/api/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!jobResponse.ok) {
        throw new Error(`Failed to fetch job: ${jobResponse.status}`);
      }
      
      const jobData = await jobResponse.json();
      setJob(jobData);
      
      // Fetch position templates
      const templatesResponse = await fetch('/api/position-templates', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const templatesData = await templatesResponse.json();
      setPositionTemplates(templatesData.templates || []);
      
      // Fetch PI profiles
      const profilesResponse = await fetch('/api/pi-profiles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const profilesData = await profilesResponse.json();
      setPiProfiles(profilesData.profiles || []);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const requestData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        job_id: parseInt(jobId || '0'),
        pi_factor_a: formData.pi_factor_a && String(formData.pi_factor_a).trim() ? parseFloat(formData.pi_factor_a) : null,
        pi_factor_b: formData.pi_factor_b && String(formData.pi_factor_b).trim() ? parseFloat(formData.pi_factor_b) : null,
        pi_factor_c: formData.pi_factor_c && String(formData.pi_factor_c).trim() ? parseFloat(formData.pi_factor_c) : null,
        pi_factor_d: formData.pi_factor_d && String(formData.pi_factor_d).trim() ? parseFloat(formData.pi_factor_d) : null,
        position_template_id: formData.position_template_id && String(formData.position_template_id).trim() ? parseInt(formData.position_template_id) : null,
        pi_profile_id: formData.pi_profile_id && String(formData.pi_profile_id).trim() ? parseInt(formData.pi_profile_id) : null,
        interview_date: formData.interview_date && String(formData.interview_date).trim() ? formData.interview_date : null,
        phone: formData.phone && String(formData.phone).trim() ? String(formData.phone).trim() : null,
        email: formData.email && String(formData.email).trim() ? String(formData.email).trim() : null,
        notes: formData.notes && String(formData.notes).trim() ? String(formData.notes).trim() : null,
        application_date: formData.application_date,
        application_source: formData.application_source,
      };
      
      console.log('Sending candidate data:', JSON.stringify(requestData, null, 2));
      
      const response = await fetch('/api/candidates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error Response:', JSON.stringify(errorData, null, 2));
        console.error('Response status:', response.status);
        console.error('Response statusText:', response.statusText);
        throw new Error(errorData.error || errorData.message || 'Failed to create candidate');
      }

      await response.json();
      setSuccess('Candidate added successfully!');
      
      // Redirect to job detail page after 2 seconds
      setTimeout(() => {
        navigate(`/jobs/${jobId}`);
      }, 2000);
      
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const validatePIFactors = () => {
    const { pi_factor_a, pi_factor_b, pi_factor_c, pi_factor_d } = formData;
    if (pi_factor_a || pi_factor_b || pi_factor_c || pi_factor_d) {
      return pi_factor_a && pi_factor_b && pi_factor_c && pi_factor_d;
    }
    return true; // It's okay to have no PI factors
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`/jobs/${jobId}`)}
        sx={{ mb: 2 }}
      >
        Back to Job
      </Button>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Person sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Add Candidate</Typography>
            {job && (
              <Typography variant="subtitle1" color="text.secondary">
                Position: {job.title} • {job.department}
              </Typography>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Basic Information */}
            <Typography variant="h6">Basic Information</Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                required
              />
              
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                required
              />
              
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
              
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </Box>

            <Divider />
            
            {/* Application Details */}
            <Typography variant="h6">Application Details</Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Position Template</InputLabel>
                <Select
                  value={formData.position_template_id}
                  onChange={(e) => handleInputChange('position_template_id', e.target.value)}
                  label="Position Template"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {positionTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Application Source</InputLabel>
                <Select
                  value={formData.application_source}
                  onChange={(e) => handleInputChange('application_source', e.target.value)}
                  label="Application Source"
                >
                  <MenuItem value="website">Company Website</MenuItem>
                  <MenuItem value="referral">Employee Referral</MenuItem>
                  <MenuItem value="recruiting">Recruiting Agency</MenuItem>
                  <MenuItem value="linkedin">LinkedIn</MenuItem>
                  <MenuItem value="indeed">Indeed</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Application Date"
                type="date"
                value={formData.application_date}
                onChange={(e) => handleInputChange('application_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Interview Date"
                type="date"
                value={formData.interview_date}
                onChange={(e) => handleInputChange('interview_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Divider />
            
            {/* PI Assessment */}
            <Box display="flex" alignItems="center">
              <Psychology sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">PI Assessment Results</Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Enter the candidate's PI factor scores (0-100). All four factors are required for position matching.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Factor A (Dominance)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                value={formData.pi_factor_a}
                onChange={(e) => handleInputChange('pi_factor_a', e.target.value)}
                helperText="0-100"
                data-testid="pi-factor-a"
              />

              <TextField
                fullWidth
                label="Factor B (Extraversion)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                value={formData.pi_factor_b}
                onChange={(e) => handleInputChange('pi_factor_b', e.target.value)}
                helperText="0-100"
                data-testid="pi-factor-b"
              />

              <TextField
                fullWidth
                label="Factor C (Patience)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                value={formData.pi_factor_c}
                onChange={(e) => handleInputChange('pi_factor_c', e.target.value)}
                helperText="0-100"
                data-testid="pi-factor-c"
              />

              <TextField
                fullWidth
                label="Factor D (Formality)"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                value={formData.pi_factor_d}
                onChange={(e) => handleInputChange('pi_factor_d', e.target.value)}
                helperText="0-100"
                data-testid="pi-factor-d"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>PI Profile</InputLabel>
                <Select
                  value={formData.pi_profile_id}
                  onChange={(e) => handleInputChange('pi_profile_id', e.target.value)}
                  label="PI Profile"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {piProfiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={profile.code} size="small" clickable={false} />
                        <span>{profile.name}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {!validatePIFactors() && (
              <Alert severity="warning">
                Please provide all four PI factors (A, B, C, D) for position matching, or leave them all empty.
              </Alert>
            )}

            {/* Notes */}
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional notes about the candidate..."
            />

            {/* Submit Button */}
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate(`/jobs/${jobId}`)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                disabled={submitting || !validatePIFactors()}
              >
                {submitting ? 'Adding...' : 'Add Candidate'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default AddCandidate;